import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, notification } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  ShopOutlined,
  TagsOutlined,
  InboxOutlined,
  FileTextOutlined,
  UserOutlined,
  CheckCircleOutlined,
  BarChartOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  BankOutlined,
  CarOutlined
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';

// Import pages
import Dashboard from '../pages/Dashboard';
import ProductList from '../pages/Products/ProductList';
import ProductForm from '../pages/Products/ProductFormNew';
import ProductView from '../pages/Products/ProductView';
import CategoryList from '../pages/Categories/CategoryList';
import CategoryForm from '../pages/Categories/CategoryForm';
import AttributeList from '../pages/Attributes/AttributeList';
import AttributeForm from '../pages/Attributes/AttributeForm';
import AttributeValueManager from '../pages/Attributes/AttributeValueManager';
import StockList from '../pages/Stock/StockList';
import StockForm from '../pages/Stock/StockForm';
import StockDashboard from '../pages/Stock/index';
import InvoiceExportList from '../pages/Invoices/InvoiceExportList';
import InvoiceImportList from '../pages/Invoices/InvoiceImportList';
import InvoiceExportForm from '../pages/Invoices/InvoiceExportForm';
import InvoiceImportForm from '../pages/Invoices/InvoiceImportForm';
import UserList from '../pages/Users/UserList';
import UserForm from '../pages/Users/UserForm';
import UserDetail from '../pages/Users/UserDetail';
import PendingApproval from '../pages/Users/PendingApproval';
import CommissionApproval from '../pages/Approvals/CommissionApproval';
import PackingList from '../pages/Approvals/PackingList';
import WithdrawalList from '../pages/Withdrawals/WithdrawalList';
import Bophankinhdoanh from '../pages/Business/Bophankinhdoanh';
import NewsList from '../pages/News/NewsList';
import NewsForm from '../pages/News/NewsForm';
import NewsView from '../pages/News/NewsView';
import Reports from '../pages/Reports/Reports';
import ReferralList from '../pages/Referrals/ReferralList';
import ShippingList from '../pages/Shipping/ShippingList';
import { authService } from '../services';
import { getUserInfo, clearAuthData } from '../utils/helpers';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Trên mobile, mặc định collapsed là true
      if (mobile) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []); // Bỏ dependency collapsed để tránh loop

  // Helper function to add debug logs
  const addDebugLog = (message) => {
    if (process.env.NODE_ENV !== 'development') return;
    
    const logEntry = `[${new Date().toISOString()}] ${message}`;
    
    // Save to sessionStorage instead of localStorage for security
    const existingLogs = JSON.parse(sessionStorage.getItem('adminDebugLogs') || '[]');
    existingLogs.push(logEntry);
    // Keep only last 20 logs
    if (existingLogs.length > 20) {
      existingLogs.shift();
    }
    sessionStorage.setItem('adminDebugLogs', JSON.stringify(existingLogs));
    
    // Update state
    setDebugLogs(prev => [...prev.slice(-19), logEntry]);
  };

  // Load existing logs on component mount (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const existingLogs = JSON.parse(sessionStorage.getItem('adminDebugLogs') || '[]');
      setDebugLogs(existingLogs);
    }
  }, []);

  useEffect(() => {
    const user = getUserInfo();
    
    if (user) {
      setUserInfo(user);
    } else {
      // Add a small delay to check again, handling potential race conditions on login
      const timer = setTimeout(() => {
        const userAfterDelay = getUserInfo();
        if (userAfterDelay) {
          setUserInfo(userAfterDelay);
        } else {
          navigate('/login');
        }
      }, 2000); // Reduced from 20 seconds to 2 seconds

      return () => {
        clearTimeout(timer);
      };
    }
  }, [navigate]);

  // Separate useEffect for handling route changes
  useEffect(() => {
    // Set openKeys based on current path
    const pathSegments = location.pathname.split('/');
    if (pathSegments.length > 2) {
      const parentKey = '/' + pathSegments[1];
      setOpenKeys([parentKey]);
    }
  }, [location.pathname]);

  // Add CSS injection for custom scrollbar
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .admin-sidebar-scrollable {
        overflow-y: auto !important;
        overflow-x: hidden;
      }
      .admin-sidebar-scrollable::-webkit-scrollbar {
        width: 8px;
        background: rgba(0, 0, 0, 0.03);
      }
      .admin-sidebar-scrollable::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
        margin: 4px 0;
      }
      .admin-sidebar-scrollable::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.25);
        border-radius: 4px;
        transition: background 0.3s ease;
        min-height: 20px;
      }
      .admin-sidebar-scrollable::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.4);
      }
      .admin-sidebar-scrollable::-webkit-scrollbar-thumb:active {
        background: rgba(0, 0, 0, 0.6);
      }
      .admin-sidebar-scrollable {
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.25) rgba(0, 0, 0, 0.05);
      }
      /* Force show scrollbar always */
      .admin-sidebar-scrollable::-webkit-scrollbar {
        -webkit-appearance: none;
        display: block !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      clearAuthData();
      navigate('/login');
      notification.success({
        message: 'Đăng xuất thành công',
        description: 'Hẹn gặp lại bạn lần sau!',
      });
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthData();
      navigate('/login');
    }
  };

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/products',
      icon: <ShopOutlined />,
      label: 'Sản phẩm',
      children: [
        {
          key: '/products/list',
          label: 'Danh sách sản phẩm',
        },
        {
          key: '/products/add',
          label: 'Thêm sản phẩm',
        },
      ],
    },
    {
      key: '/categories',
      icon: <TagsOutlined />,
      label: 'Phân loại',
      children: [
        {
          key: '/categories/list',
          label: 'Danh sách phân loại',
        },
        {
          key: '/categories/add',
          label: 'Thêm phân loại',
        },
      ],
    },
    {
      key: '/attributes',
      icon: <SettingOutlined />,
      label: 'Thuộc tính',
      children: [
        {
          key: '/attributes/list',
          label: 'Danh sách thuộc tính',
        },
        {
          key: '/attributes/add',
          label: 'Thêm thuộc tính',
        },
      ],
    },
    {
      key: '/stock',
      icon: <InboxOutlined />,
      label: 'Quản lý kho',
      children: [
        {
          key: '/stock/dashboard',
          label: 'Tổng quan kho',
        },
        {
          key: '/stock/list',
          label: 'Danh sách tồn kho',
        },
        {
          key: '/stock/adjustment',
          label: 'Điều chỉnh kho',
        },
      ],
    },
    {
      key: '/invoices',
      icon: <FileTextOutlined />,
      label: 'Hóa đơn',
      children: [
        {
          key: '/invoices/export',
          label: 'Hóa đơn bán hàng',
        },
        {
          key: '/invoices/export/add',
          label: 'Mua hàng tại quầy',
        },
        {
          key: '/invoices/import',
          label: 'Hóa đơn nhập hàng',
        },
        {
          key: '/invoices/import/add',
          label: 'Tạo hóa đơn nhập',
        },
      ],
    },
    {
      key: '/shipping',
      icon: <CarOutlined />,
      label: 'Tạo vận đơn',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Người dùng',
      children: [
        {
          key: '/users/list',
          label: 'Danh sách người dùng',
        },
        {
          key: '/users/add',
          label: 'Thêm người dùng',
        },
        {
          key: '/users/pending-approval',
          label: 'Nhân Sự cần Duyệt',
        },
        {
          key: '/referrals',
          label: 'Người giới thiệu',
        },
      ],
    },
    {
      key: '/approvals',
      icon: <CheckCircleOutlined />,
      label: 'Duyệt đơn',
      children: [
        {
          key: '/approvals/commission',
          label: 'Tinh thù lao',
        },
        {
          key: '/approvals/packing',
          label: 'Đóng gói hàng',
        },
      ],
    },
    {
      key: '/business',
      icon: <BarChartOutlined />,
      label: 'Phòng Kinh Doanh',
      children: [
        {
          key: '/business/department',
          label: 'Quản lý phòng ban',
        }
      ]
    },
    {
      key: '/withdrawals',
      icon: <BankOutlined />,
      label: 'Rút Tiền',
    },
    {
      key: '/news',
      icon: <FileTextOutlined />,
      label: 'Tin tức',
      children: [
        {
          key: '/news/list',
          label: 'Danh sách tin tức',
        },
        {
          key: '/news/add',
          label: 'Thêm tin tức',
        },
      ],
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Báo cáo',
      children: [
        {
          key: '/reports/sales',
          label: 'Báo cáo bán hàng',
        },
        {
          key: '/reports/inventory',
          label: 'Báo cáo tồn kho',
        },
        {
          key: '/reports/customers',
          label: 'Báo cáo khách hàng',
        },
        {
          key: '/reports/revenue',
          label: 'Báo cáo doanh thu',
        },
      ],
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Cài đặt',
      children: [
        {
          key: '/settings/general',
          label: 'Cài đặt chung',
        },
        {
          key: '/settings/email',
          label: 'Cài đặt email',
        },
        {
          key: '/settings/payment',
          label: 'Cài đặt thanh toán',
        },
        {
          key: '/settings/shipping',
          label: 'Cài đặt vận chuyển',
        },
      ],
    },
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Thông tin cá nhân',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Cài đặt',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Đăng xuất',
      onClick: handleLogout,
    },
  ];

  const handleMenuClick = ({ key }) => {
    try {
      navigate(key);
      
      // Đóng menu trên mobile sau khi navigate
      if (isMobile) {
        setTimeout(() => {
          setCollapsed(true);
        }, 100);
      }
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const getPageTitle = () => {
    const path = location.pathname;
    
    // Check for dynamic routes
    if (path.startsWith('/products/view/')) {
      return 'Chi tiết sản phẩm';
    }
    if (path.startsWith('/products/edit/')) {
      return 'Chỉnh sửa sản phẩm';
    }
    if (path.startsWith('/categories/edit/')) {
      return 'Chỉnh sửa phân loại';
    }
    if (path.startsWith('/attributes/edit/')) {
      return 'Chỉnh sửa thuộc tính';
    }
    if (path.startsWith('/attributes/') && path.includes('/values')) {
      return 'Quản lý giá trị thuộc tính';
    }
    if (path.startsWith('/users/view/')) {
      return 'Chi tiết người dùng';
    }
    if (path.startsWith('/news/view/')) {
      return 'Xem chi tiết tin tức';
    }
    if (path.startsWith('/news/edit/')) {
      return 'Chỉnh sửa tin tức';
    }
    
    const titleMap = {
      '/': 'Dashboard',
      '/products/list': 'Danh sách sản phẩm',
      '/products/add': 'Thêm sản phẩm',
      '/categories/list': 'Danh sách phân loại',
      '/categories/add': 'Thêm phân loại',
      '/attributes/list': 'Danh sách thuộc tính',
      '/attributes/add': 'Thêm thuộc tính',
      '/stock/dashboard': 'Tổng quan quản lý kho',
      '/stock/list': 'Danh sách tồn kho',
      '/stock/adjustment': 'Điều chỉnh kho',
      '/invoices/export': 'Hóa đơn xuất',
      '/invoices/export/add': 'Tạo hóa đơn xuất',
      '/invoices/import': 'Hóa đơn nhập',
      '/invoices/import/add': 'Tạo hóa đơn nhập',
      '/shipping': 'Tạo vận đơn',
      '/users/list': 'Danh sách người dùng',
      '/users/add': 'Thêm người dùng',
      '/users/pending-approval': 'Nhân Sự cần Duyệt',
      '/referrals': 'Danh sách người giới thiệu',
      '/approvals/commission': 'Tinh thù lao',
      '/approvals/packing': 'Đóng gói hàng',
      '/news/list': 'Danh sách tin tức',
      '/news/add': 'Thêm tin tức',
      '/reports': 'Báo cáo',
    };
    return titleMap[path] || 'Admin Portal';
  };

  if (!userInfo) {
    return null;
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        breakpoint="lg"
        collapsedWidth={0}
        onBreakpoint={(broken) => {
          // Không tự động collapse khi breakpoint thay đổi
        }}
        style={{
          position: 'fixed',
          left: collapsed ? (isMobile ? '-300px' : '0') : '0',
          top: 0,
          bottom: 0,
          zIndex: 1001,
          width: 300, // Increased from 200 to 250
          overflow: 'hidden',
          transform: collapsed ? 'translateX(0)' : 'translateX(0)',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header Section - Fixed */}
        <div style={{ 
          height: 64, 
          padding: '16px', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          flexShrink: 0
        }}>
          <Title 
            level={4} 
            style={{ 
              margin: 0,
              background: 'linear-gradient(135deg, #334155 0%, #475569 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
              fontSize: collapsed ? '14px' : '16px'
            }}
          >
            {collapsed ? 'AP' : 'Admin Portal'}
          </Title>
        </div>
        
        {/* Menu Section - Scrollable */}
        <div 
          className="admin-sidebar-scrollable"
          style={{ 
            flex: '1 1 auto', 
            overflow: 'auto',
            minHeight: 0, // Important for flex child to be scrollable
            maxHeight: 'none', // Remove height restriction
            height: 'calc(100vh - 64px - 80px)', // Increase available height by reducing logo space
            position: 'relative'
          }}
        >
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              height: 'auto', // Let menu expand naturally
              minHeight: 'calc(100vh - 64px - 80px)' // Ensure menu can take full available space
            }}
          />
        </div>
        
        {/* Company Logo Section - Fixed at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px', // Reduced height to give more space to menu
          padding: collapsed ? '6px 4px' : '8px 12px',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          textAlign: 'center',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          
        </div>
      </Sider>
      <Layout style={{ 
        marginLeft: collapsed ? 0 : (isMobile ? 0 : 250), // Updated to match new sidebar width
        transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <Header style={{ 
          padding: '0 24px', 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 2px 20px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 99,
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {React.createElement(
              collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
              {
                style: { 
                  fontSize: '18px', 
                  cursor: 'pointer',
                  color: '#64748b',
                  transition: 'all 0.3s ease'
                },
                onClick: () => setCollapsed(!collapsed),
              }
            )}
            <Title level={4} style={{ 
              margin: '0 0 0 16px',
              fontSize: isMobile ? '16px' : '18px',
              background: 'linear-gradient(135deg, #1a1a1a 0%, #4a4a4a 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              {getPageTitle()}
            </Title>
          </div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 8 : 16 
          }}>
            <BellOutlined style={{ 
              fontSize: '18px', 
              cursor: 'pointer',
              color: '#64748b',
              transition: 'all 0.3s ease'
            }} />
           
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background-color 0.3s'
              }}>
                <Avatar icon={<UserOutlined />} />
                <span>{userInfo.username}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        
        {/* Mobile Overlay */}
        {isMobile && !collapsed && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              backdropFilter: 'blur(4px)'
            }}
            onClick={() => setCollapsed(true)}
          />
        )}
        
        <Content style={{ 
          margin: 0,
          transition: 'all 0.3s ease',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 64px)' // Trừ đi header height
        }}>
          <div style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/products/list" element={<ProductList />} />
              <Route path="/products/add" element={<ProductForm />} />
              <Route path="/products/edit/:id" element={<ProductForm />} />
              <Route path="/products/view/:id" element={<ProductView />} />
              <Route path="/categories/list" element={<CategoryList />} />
              <Route path="/categories/add" element={<CategoryForm />} />
              <Route path="/categories/edit/:id" element={<CategoryForm />} />
              <Route path="/attributes/list" element={<AttributeList />} />
              <Route path="/attributes" element={<AttributeList />} />
              <Route path="/attributes/add" element={<AttributeForm />} />
              <Route path="/attributes/edit/:id" element={<AttributeForm />} />
              <Route path="/attributes/:id/values" element={<AttributeValueManager />} />
              <Route path="/stock/dashboard" element={<StockDashboard />} />
              <Route path="/stock/list" element={<StockList />} />
              <Route path="/stock/adjustment" element={<StockForm />} />
              <Route path="/invoices/export" element={<InvoiceExportList />} />
              <Route path="/invoices/export/add" element={<InvoiceExportForm />} />
              <Route path="/invoices/export/edit/:id" element={<InvoiceExportForm />} />
              <Route path="/invoices/import" element={<InvoiceImportList />} />
              <Route path="/invoices/import/add" element={<InvoiceImportForm />} />
              <Route path="/invoices/import/edit/:id" element={<InvoiceImportForm />} />
              <Route path="/shipping" element={<ShippingList />} />
              <Route path="/users/list" element={<UserList />} />
              <Route path="/users/add" element={<UserForm />} />
              <Route path="/users/view/:id" element={<UserDetail />} />
              <Route path="/users/pending-approval" element={<PendingApproval />} />
              <Route path="/referrals" element={<ReferralList />} />
              <Route path="/approvals/commission" element={<CommissionApproval />} />
              <Route path="/approvals/packing" element={<PackingList />} />
              <Route path="/business/department" element={<Bophankinhdoanh />} />
              <Route path="/withdrawals" element={<WithdrawalList />} />
              <Route path="/news/list" element={<NewsList />} />
              <Route path="/news/add" element={<NewsForm />} />
              <Route path="/news/edit/:id" element={<NewsForm />} />
              <Route path="/news/view/:id" element={<NewsView />} />
              <Route path="/reports" element={<Reports />} />
              {/* Catch all other routes and redirect to dashboard */}
              <Route path="*" element={<Dashboard />} />
            </Routes>
          </div>
          
       
        </Content>
      </Layout>
      
      {/* Debug Modal - Development Only */}
      {process.env.NODE_ENV === 'development' && showDebug && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '80%',
            maxHeight: '80%',
            overflow: 'auto',
            minWidth: '600px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>Debug Logs (Development)</h3>
              <button 
                onClick={() => setShowDebug(false)}
                style={{ background: 'red', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px',
              maxHeight: '400px',
              overflow: 'auto'
            }}>
              {debugLogs.length === 0 ? 'No logs available...' : debugLogs.map((log, index) => (
                <div key={index} style={{ marginBottom: '4px', wordBreak: 'break-word' }}>
                  {log}
                </div>
              ))}
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button 
                onClick={() => {
                  sessionStorage.removeItem('adminDebugLogs');
                  setDebugLogs([]);
                }}
                style={{ background: 'gray', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
              >
                Clear Logs
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminLayout;
